/**
 * Pricing Constants & Utilities
 *
 * All prices are in CHF (Swiss Francs).
 * Internal values stored in cents (centimes) for precision.
 */

import type { StartupTier, InvestorTier, BillingInterval } from './types';
import { STARTUP_TIERS, INVESTOR_TIERS } from './types';

// ============================================================================
// Currency Configuration
// ============================================================================

export const CURRENCY = {
  code: 'CHF',
  symbol: 'CHF',
  name: 'Swiss Franc',
  locale: 'de-CH', // Swiss German locale for formatting
  decimalPlaces: 2,
} as const;

// ============================================================================
// Discount Configuration
// ============================================================================

export const ANNUAL_DISCOUNT_PERCENT = 20;

/**
 * Calculate annual price with discount
 * @param monthlyPriceCents Monthly price in cents
 * @returns Annual price in cents (with 20% discount)
 */
export function calculateAnnualPrice(monthlyPriceCents: number): number {
  const fullAnnualPrice = monthlyPriceCents * 12;
  const discount = fullAnnualPrice * (ANNUAL_DISCOUNT_PERCENT / 100);
  return Math.round(fullAnnualPrice - discount);
}

/**
 * Calculate savings from annual billing
 * @param monthlyPriceCents Monthly price in cents
 * @returns Savings in cents per year
 */
export function calculateAnnualSavings(monthlyPriceCents: number): number {
  const fullAnnualPrice = monthlyPriceCents * 12;
  const discountedPrice = calculateAnnualPrice(monthlyPriceCents);
  return fullAnnualPrice - discountedPrice;
}

// ============================================================================
// Price Formatting
// ============================================================================

/**
 * Format price in cents to display string
 * @param cents Price in cents (centimes)
 * @param options Formatting options
 * @returns Formatted price string (e.g., "CHF 29.00")
 */
export function formatPrice(
  cents: number,
  options: {
    showCurrency?: boolean;
    showDecimals?: boolean;
    compact?: boolean;
  } = {}
): string {
  const { showCurrency = true, showDecimals = true, compact = false } = options;

  const amount = cents / 100;

  if (compact && amount >= 1000) {
    const formatted = (amount / 1000).toFixed(1).replace(/\.0$/, '');
    return showCurrency ? `${CURRENCY.symbol} ${formatted}k` : `${formatted}k`;
  }

  const formatted = showDecimals
    ? amount.toLocaleString(CURRENCY.locale, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    : amount.toLocaleString(CURRENCY.locale, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      });

  return showCurrency ? `${CURRENCY.symbol} ${formatted}` : formatted;
}

/**
 * Format price with billing interval
 * @param cents Price in cents
 * @param interval Billing interval
 * @returns Formatted string (e.g., "CHF 29/mo" or "CHF 278/yr")
 */
export function formatPriceWithInterval(
  cents: number,
  interval: BillingInterval
): string {
  const price = formatPrice(cents, { showDecimals: false });
  const suffix = interval === 'month' ? '/mo' : '/yr';
  return `${price}${suffix}`;
}

/**
 * Format price range
 * @param minCents Minimum price in cents
 * @param maxCents Maximum price in cents
 * @returns Formatted range (e.g., "CHF 29 - 99")
 */
export function formatPriceRange(minCents: number, maxCents: number): string {
  const min = formatPrice(minCents, { showDecimals: false });
  const max = formatPrice(maxCents, { showCurrency: false, showDecimals: false });
  return `${min} - ${max}`;
}

// ============================================================================
// Mock Stripe Price IDs
// ============================================================================

/**
 * Mock Stripe price IDs for development
 * In production, these would come from Stripe Dashboard
 */
export const MOCK_STRIPE_PRICE_IDS = {
  startup: {
    free: {
      month: null, // Free tier has no price ID
      year: null,
    },
    starter: {
      month: 'price_startup_starter_monthly',
      year: 'price_startup_starter_yearly',
    },
    standard: {
      month: 'price_startup_standard_monthly',
      year: 'price_startup_standard_yearly',
    },
    premium: {
      month: 'price_startup_premium_monthly',
      year: 'price_startup_premium_yearly',
    },
  },
  investor: {
    free: {
      month: null,
      year: null,
    },
    starter: {
      month: 'price_investor_starter_monthly',
      year: 'price_investor_starter_yearly',
    },
    standard: {
      month: 'price_investor_standard_monthly',
      year: 'price_investor_standard_yearly',
    },
    premium: {
      month: 'price_investor_premium_monthly',
      year: 'price_investor_premium_yearly',
    },
  },
} as const;

// ============================================================================
// Price Lookup Functions
// ============================================================================

/**
 * Get price for a startup tier
 */
export function getStartupPrice(
  tier: StartupTier,
  interval: BillingInterval
): number {
  const config = STARTUP_TIERS[tier];
  return interval === 'month' ? config.monthlyPrice : config.yearlyPrice;
}

/**
 * Get price for an investor tier
 */
export function getInvestorPrice(
  tier: InvestorTier,
  interval: BillingInterval
): number {
  const config = INVESTOR_TIERS[tier];
  return interval === 'month' ? config.monthlyPrice : config.yearlyPrice;
}

/**
 * Get the Stripe price ID for a tier
 */
export function getStripePriceId(
  userRole: 'startup' | 'investor',
  tier: StartupTier | InvestorTier,
  interval: BillingInterval
): string | null {
  if (userRole === 'startup') {
    return MOCK_STRIPE_PRICE_IDS.startup[tier as StartupTier]?.[interval] ?? null;
  }
  return MOCK_STRIPE_PRICE_IDS.investor[tier as InvestorTier]?.[interval] ?? null;
}

/**
 * Calculate upgrade/downgrade price difference
 */
export function calculatePriceDifference(
  currentTier: StartupTier | InvestorTier,
  newTier: StartupTier | InvestorTier,
  interval: BillingInterval,
  userRole: 'startup' | 'investor'
): {
  difference: number;
  isUpgrade: boolean;
  formatted: string;
} {
  const currentPrice =
    userRole === 'startup'
      ? getStartupPrice(currentTier as StartupTier, interval)
      : getInvestorPrice(currentTier as InvestorTier, interval);

  const newPrice =
    userRole === 'startup'
      ? getStartupPrice(newTier as StartupTier, interval)
      : getInvestorPrice(newTier as InvestorTier, interval);

  const difference = newPrice - currentPrice;
  const isUpgrade = difference > 0;

  return {
    difference: Math.abs(difference),
    isUpgrade,
    formatted: formatPrice(Math.abs(difference)),
  };
}

// ============================================================================
// Referral Discount
// ============================================================================

export const REFERRAL_DISCOUNT_PERCENT = 20;

/**
 * Calculate referral discount
 * @param priceCents Original price in cents
 * @returns Discounted price in cents
 */
export function applyReferralDiscount(priceCents: number): number {
  const discount = priceCents * (REFERRAL_DISCOUNT_PERCENT / 100);
  return Math.round(priceCents - discount);
}

/**
 * Format referral savings
 */
export function formatReferralSavings(priceCents: number): string {
  const savings = priceCents * (REFERRAL_DISCOUNT_PERCENT / 100);
  return formatPrice(Math.round(savings));
}

// ============================================================================
// Trial Configuration
// ============================================================================

export const TRIAL_DAYS = 14;

/**
 * Calculate trial end date
 */
export function getTrialEndDate(startDate: Date = new Date()): Date {
  const endDate = new Date(startDate);
  endDate.setDate(endDate.getDate() + TRIAL_DAYS);
  return endDate;
}

/**
 * Format remaining trial days
 */
export function formatTrialRemaining(endDate: Date): string {
  const now = new Date();
  const diffMs = endDate.getTime() - now.getTime();
  const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays <= 0) return 'Trial ended';
  if (diffDays === 1) return '1 day left';
  return `${diffDays} days left`;
}
